import { breathStabilityScore } from '@muzly/voice-engine';

import { overallScore } from '../onboarding/record';

import type { ScoredMetricKey } from '../onboarding/metricGrades';
import type { DiagnosisRecord } from '../onboarding/record';

/**
 * recentSummary — 마지막 회차 → 홈이 그리는 한 장짜리 요약(순수 변환).
 *
 * 홈은 회차 전체가 아니라 **덱 p8의 네 줄**만 낸다(종합·호흡·음역·음정). 그래도 변환을
 * 화면 밖에 두는 이유는 종합 점수 산식이 결과 화면(슬라이스 12)과 **같아야** 하기 때문이다 —
 * 두 화면이 각자 평균을 내면 같은 회차가 홈에서 78, 결과에서 79가 된다.
 *
 * 없는 값은 전부 null이다(0 아님). 미측정 축을 0으로 채우면 "그때는 0점이었다"가 되고,
 * 그건 저장 계층이 애써 지킨 규약(hnrDb "없으면 필드가 없다")을 화면에서 무너뜨린다.
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */

/**
 * 홈이 내는 저장 점수축. 호흡은 저장 점수(`sustain`)가 아니라 구간 평균이라 `breath`로 따로
 * 내고, 음역은 점수가 아니라 사실이라 여기 없다(rangeLabel).
 */
export const HOME_METRIC_KEYS: ScoredMetricKey[] = ['pitchAccuracy'];

export interface RecentScore {
  key: ScoredMetricKey;
  /** 그 회차에 그 축이 없었으면 null → 화면은 "이번엔 못 쟀어요". */
  val: number | null;
}

export interface RecentSummary {
  completedAt: number;
  /**
   * 측정된 축의 **균등 평균**(미보정 — 가중치 미정, 플랜 What's NOT). 축이 하나도
   * 없으면 null이다. 분모는 저장된 축 전부지 화면에 내는 두 개가 아니다.
   * 산식 정본은 `record/toDiagnosisRecord.overallScore` — 결과 화면이 같은 함수를 부른다.
   */
  overall: number | null;
  /**
   * 호흡 안정도 — 결과 탭 머리·홈 타일과 같은 구간 평균(`breathStabilityScore`). `sustain`
   * (지속 시간)을 여기 두면 같은 이름 아래 숫자가 갈린다(TestFlight 3차 #108).
   */
  breath: number | null;
  scores: RecentScore[];
  /** `E2 ~ C5`. 저음·고음이 다 유효했던 회차만. */
  rangeLabel: string | null;
}

export const toRecentSummary = (
  record: DiagnosisRecord | null,
): RecentSummary | null => {
  if (!record) {
    return null;
  }
  return {
    completedAt: record.completedAt,
    overall: overallScore(record),
    breath: breathStabilityScore(record.breath ?? []),
    scores: HOME_METRIC_KEYS.map((key) => ({
      key,
      val: record.scores.find((s) => s.key === key)?.val ?? null,
    })),
    rangeLabel:
      record.range?.lowNote && record.range?.highNote
        ? `${record.range.lowNote} ~ ${record.range.highNote}`
        : null,
  };
};
